import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  of,
  share,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { ErrorHandlingService } from '../../core/services/error-handling.service';
import {
  OrgUserQueryParams,
  StudentSearchModel,
} from '../manage-organization/insights/insights.interface';
import {
  ApiResponse,
  ApiResponses,
  BIPApiResponsePaginated,
  BIPQueryParams,
  BipTemplateListModel,
  CaseDetailModel,
  CreateBipModel,
  CreatePreBipModel,
  IncidentApiResponsePaginated,
  IncidentList,
  IncidentModel,
  IncidentQueryParams,
  PreBIPApiResponsePaginated,
  PreBIPQueryParams,
  ConvertToBIP,
  MoveStudentRequest,
  UnAssignCase,
  User,
} from './insights.interface';
import {
  BehaviorFuncation,
  BehaviouralAssessmentGraphData,
  FunctionalAssessmentModelAnswer,
  FunctionalAssessmentModelResponse,
  FunctionalAssessmentResponse,
} from './behavior-intervention-plan/view-bip-case/assessment-fba/assesment-fba.interface';
import { userDetailModel } from '../organizations/organization.interface';
import { AuthService } from '../../core/services/auth.service';
import { ApiStratergyResponses } from './behavior-intervention-plan/view-bip-case/quick-guide/quick-guide.interface';
import { ApiRespones } from '../manage-organization/settings/settings.interface';
import { AuditFieldChange } from '../audit-logs/audit-logs.interface';

@Injectable({
  providedIn: 'root',
})
export class InsightsService {
  private readonly API_URL = `${environment.apiUrl}`;
  private user_Data: userDetailModel | null = null;
  assessmentSubject = new BehaviorSubject<FunctionalAssessmentModelAnswer[]>(
    []
  );
  assessment$ = this.assessmentSubject.asObservable();
  private readonly preBIPSubject =
    new BehaviorSubject<PreBIPApiResponsePaginated | null>(null);
  preBIP$ = this.preBIPSubject.asObservable();

  private readonly BIPSubject =
    new BehaviorSubject<BIPApiResponsePaginated | null>(null);
  BIP$ = this.BIPSubject.asObservable();

  public totalCasesSubject = new BehaviorSubject<number>(0);
  totalCases$ = this.totalCasesSubject.asObservable();

  private readonly IncidentSubject =
    new BehaviorSubject<IncidentApiResponsePaginated | null>(null);
  Incident$ = this.IncidentSubject.asObservable();

  private readonly activePreBIPRequests = new Map<
    string,
    Observable<PreBIPApiResponsePaginated>
  >();
  private lastPreBIPRequestTime = 0;
  private readonly DEBOUNCE_TIME = 300;

  private preBIPListFilterOrgId: number | null | undefined;

  private bipListFilterOrgId: number | null | undefined;

  private readonly activeBIPRequests = new Map<
    string,
    Observable<BIPApiResponsePaginated>
  >();
  private lastBIPRequestTime = 0;

  private readonly activeIncidentRequests = new Map<
    string,
    Observable<IncidentApiResponsePaginated>
  >();
  private lastIncidentRequestTime = 0;

  constructor(
    private readonly fb: FormBuilder,
    private readonly errorHandler: ErrorHandlingService,
    private readonly http: HttpClient,
    private readonly authService: AuthService
  ) {
    this.authService.userData$.subscribe(res => {
      this.user_Data = res;
    });
  }

  setPreBIPListFilterOrganization(organizationId: number | null | undefined) {
    this.preBIPListFilterOrgId = organizationId;
  }
  setBIPListFilterOrganization(organizationId: number | null | undefined) {
    this.bipListFilterOrgId = organizationId;
  }

  private captureFormValues(form: FormGroup): Record<string, unknown> {
    return {
      specialEducations: form.get('specialEducations')?.value,
      generalEducations: form.get('generalEducations')?.value,
      languageLevelId: form.get('languageLevelId')?.value,
      note: form.get('note')?.value,
      isTrainingStudent: form.get('isTrainingStudent')?.value,
      isEnabledSafetyPlan: form.get('isEnabledSafetyPlan')?.value,
      bipTemplateId: form.get('bipTemplateId')?.value,
      customScheduleId: form.get('customScheduleId')?.value,
      caseBehaviorId: form.get('caseBehaviorId')?.value,
      caseBehaviorsType: form.get('caseBehaviorsType')?.value,
    };
  }

  private restoreFormValues(
    form: FormGroup,
    values: Record<string, unknown>
  ): void {
    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== null && form.get(key)) {
        form.get(key)?.setValue(value);
      }
    });
  }

  private enableStudentFields(form: FormGroup): void {
    ['firstName', 'lastName', 'genderId', 'dateOfBirth', 'raceId'].forEach(
      ctrl => {
        if (form.controls[ctrl].disabled) {
          form.controls[ctrl].enable();
        }
      }
    );
  }

  private disableAndPopulateStudentFields(
    form: FormGroup,
    data: StudentSearchModel
  ): void {
    ['firstName', 'lastName', 'genderId', 'dateOfBirth', 'raceId'].forEach(
      (ctrl: string) => {
        form.controls[ctrl].disable();
        form.controls[ctrl].setValue(data[ctrl as keyof StudentSearchModel]);
      }
    );
  }

  bipForm(
    value?: CreateBipModel | CreatePreBipModel | null,
    omitFields: string[] = [],
    isEditMode = false
  ) {
    const fields = {
      studentId: [
        null,
        [
          Validators.required,
          Validators.pattern('^[a-zA-Z0-9-_]*[a-zA-Z0-9-_]$'),
        ],
      ],
      organizationId: [null, Validators.required],
      firstName: [
        '',
        {
          validators: [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(100),
            Validators.pattern(String.raw`^[a-zA-Z][a-zA-Z\s]*[a-zA-Z]$`),
          ],
        },
      ],
      lastName: [
        '',
        {
          validators: [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(100),
            Validators.pattern(String.raw`^[a-zA-Z][a-zA-Z\s]*[a-zA-Z]$`),
          ],
        },
      ],
      genderId: [null, [Validators.required]],
      dateOfBirth: ['', [Validators.required]],
      raceId: [null, [Validators.required]],
      caseOwnerId: [this.user_Data?.id || null, [Validators.required]],
      bipTemplateId: [{ value: null, disabled: true }, [Validators.required]],
      languageLevelId: [null, [Validators.required]],
      note: ['', [Validators.maxLength(500)]],
      isTrainingStudent: [false],
      isEnabledSafetyPlan: [false],
      specialEducations: [[]],
      generalEducations: [[]],
      caseBehaviorId: [null, [Validators.required]],
      caseBehaviorsType: [{ value: [], disabled: true }, [Validators.required]],
      customScheduleId: [
        { value: null, disabled: true },
        [Validators.required],
      ],
    };

    omitFields.forEach(f => delete fields[f as keyof typeof fields]);

    const form: FormGroup = this.fb.group(fields);
    if (value) {
      if (fields.bipTemplateId) {
        form.controls['bipTemplateId'].enable();
      } else {
        form.controls['caseBehaviorsType'].enable();
      }
      form.patchValue(value);
      if (!isEditMode) {
        [
          'studentId',
          'organizationId',
          'firstName',
          'lastName',
          'genderId',
          'dateOfBirth',
          'raceId',
        ].forEach(ctrl => {
          form.controls[ctrl].disable();
        });
      }
    }

    return form;
  }

  createPreBipCase(
    payload: CreatePreBipModel
  ): Observable<ApiResponse<CreatePreBipModel>> {
    return this.http
      .post<
        ApiResponse<CreatePreBipModel>
      >(`${this.API_URL}/insight/create-pre-bip-case`, payload)
      .pipe(
        tap(() => {
          const queryParams: PreBIPQueryParams = {
            pageIndex: 1,
            pageSize: 10,
          };

          const effectiveOrgId = this.preBIPListFilterOrgId;
          if (!this.user_Data?.isSuperAdmin && effectiveOrgId) {
            queryParams['organizationId'] = effectiveOrgId;
          }

          this.getPreBIPList(queryParams).subscribe();
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  createBipCase(payload: CreateBipModel): Observable<ApiResponse<number>> {
    return this.http
      .post<
        ApiResponse<number>
      >(`${this.API_URL}/insight/create-bip-case`, payload)
      .pipe(
        tap(() => {
          const queryParams: BIPQueryParams = {
            pageIndex: 1,
            pageSize: 10,
            IsInsightAdminTab: false,
          };

          const effectiveOrgId = this.bipListFilterOrgId;
          if (!this.user_Data?.isSuperAdmin && effectiveOrgId) {
            queryParams['organizationId'] = effectiveOrgId;
          }

          this.getBIPList(queryParams).subscribe();
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  editBipCase(
    payload: CreateBipModel,
    caseId: number
  ): Observable<ApiResponse<CaseDetailModel>> {
    return this.http
      .put<
        ApiResponse<CaseDetailModel>
      >(`${this.API_URL}/insight/update-bip-case?caseId=${caseId}`, payload)
      .pipe(
        tap(() => {
          const queryParams: BIPQueryParams = {
            pageIndex: 1,
            pageSize: 10,
            IsInsightAdminTab: false,
          };
          const effectiveOrgId = this.bipListFilterOrgId;
          if (!this.user_Data?.isSuperAdmin && effectiveOrgId) {
            queryParams['organizationId'] = effectiveOrgId;
          }

          this.getBIPList(queryParams).subscribe();
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  editPreBipCase(
    payload: CreatePreBipModel,
    caseId: number
  ): Observable<ApiResponse<CreatePreBipModel>> {
    return this.http
      .put<
        ApiResponse<CreatePreBipModel>
      >(`${this.API_URL}/insight/update-pre-bip-case?caseId=${caseId}`, payload)
      .pipe(
        tap(() => {
          const queryParams: PreBIPQueryParams = {
            pageIndex: 1,
            pageSize: 10,
          };
          const effectiveOrgId = this.preBIPListFilterOrgId;
          if (!this.user_Data?.isSuperAdmin && effectiveOrgId) {
            queryParams['organizationId'] = effectiveOrgId;
          }

          this.getPreBIPList(queryParams).subscribe();
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  searchStudentDetail(
    searchText: string | null,
    studentId?: string,
    organizationId?: number
  ): Observable<ApiResponses<StudentSearchModel>> {
    let url = `${this.API_URL}/student/search-student`;
    const params: string[] = [];
    if (organizationId) {
      params.push(`orgId=${organizationId}`);
    }
    if (searchText) {
      params.push(`name=${searchText.toString()}`);
    }
    if (studentId) {
      params.push(`studentId=${studentId}`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    return this.http
      .get<ApiResponses<StudentSearchModel>>(url)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  checkStudentIdExists(studentId: string): Observable<boolean> {
    const params = new HttpParams().set('studentId', studentId);

    return this.http
      .get<ApiResponse<boolean>>(
        `${this.API_URL}/insight/check-student-id-exists`,
        {
          params,
        }
      )
      .pipe(
        map(response => !!response.result),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  bindStudentIdExistsValidation(
    studentIdControl: AbstractControl | null,
    destroy$: Observable<void>,
    isEditMode: boolean
  ): void {
    if (!studentIdControl || isEditMode) {
      return;
    }

    studentIdControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((studentId: string | null) => {
          if (
            !studentId ||
            studentIdControl.hasError('required') ||
            studentIdControl.hasError('pattern')
          ) {
            this.clearStudentIdExistsError(studentIdControl);
            return of(false);
          }

          return this.checkStudentIdExists(studentId);
        }),
        takeUntil(destroy$)
      )
      .subscribe(exists => {
        if (exists) {
          this.setStudentIdExistsError(studentIdControl);
          return;
        }

        this.clearStudentIdExistsError(studentIdControl);
      });
  }

  private setStudentIdExistsError(control: AbstractControl): void {
    const existingErrors = control.errors ?? {};

    if (existingErrors['studentIdExists']) {
      return;
    }

    control.setErrors({
      ...existingErrors,
      studentIdExists: true,
    });
  }

  private clearStudentIdExistsError(control: AbstractControl): void {
    if (!control.errors?.['studentIdExists']) {
      return;
    }

    const remainingErrors = { ...control.errors };
    delete remainingErrors['studentIdExists'];

    control.setErrors(
      Object.keys(remainingErrors).length > 0 ? remainingErrors : null
    );
  }

  getBipTemplates(
    organizationId: number
  ): Observable<ApiResponses<BipTemplateListModel>> {
    return this.http.get<ApiResponses<BipTemplateListModel>>(
      `${this.API_URL}/biptemplate/get-by-org-id?orgId=${organizationId}`
    );
  }

  private buildPreBIPQueryParams({
    pageIndex,
    pageSize,
    sortOrder,
    sort,
    searchTerm,
    organizationId,
  }: PreBIPQueryParams): HttpParams {
    let params = new HttpParams()
      .set('Page', pageIndex.toString())
      .set('PageSize', pageSize.toString());

    if (sortOrder) {
      params = params.set('sortOrder', sortOrder);
    }

    if (sort) {
      params = params.set('sort', sort);
    }

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    if (organizationId) {
      params = params.set('organizationId', organizationId.toString());
    }
    return params;
  }

  getPreBIPList(
    queryParams: PreBIPQueryParams
  ): Observable<PreBIPApiResponsePaginated> {
    if (
      !queryParams.organizationId &&
      !this.user_Data?.isSuperAdmin &&
      this.preBIPListFilterOrgId
    ) {
      queryParams = {
        ...queryParams,
        organizationId: this.preBIPListFilterOrgId,
      };
    }
    const params = this.buildPreBIPQueryParams(queryParams);
    const requestKey = params.toString();
    const now = Date.now();

    if (now - this.lastPreBIPRequestTime < this.DEBOUNCE_TIME) {
      const currentValue = this.preBIPSubject.getValue();
      if (currentValue) {
        return of(currentValue);
      }
    }

    this.lastPreBIPRequestTime = now;

    const activeRequest = this.activePreBIPRequests.get(requestKey);
    if (activeRequest) {
      return activeRequest;
    }

    const request = this.http
      .get<PreBIPApiResponsePaginated>(
        `${this.API_URL}/Insight/list-pre-bip-case`,
        {
          params,
        }
      )
      .pipe(
        tap(response => this.preBIPSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error)),
        share(),
        finalize(() => {
          this.activePreBIPRequests.delete(requestKey);
        })
      );
    this.activePreBIPRequests.set(requestKey, request);

    return request;
  }

  getCaseById(caseId: number) {
    return this.http
      .get<
        ApiResponse<CaseDetailModel>
      >(`${this.API_URL}/insight/get-by-case-id?caseId=${caseId}`)
      .pipe(
        tap((response: ApiResponse<CaseDetailModel>) => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  private buildBIPQueryParams({
    pageIndex,
    pageSize,
    sortOrder,
    sort,
    searchTerm,
    organizationId,
    statusId,
    studentId,
    intensityId,
    isOpen,
    assessmentFba,
    studentName,
    caseOwner,
    createdDate,
    modifiedDate,
    IsInsightAdminTab,
    isMyCases,
    isUnassigned,
  }: BIPQueryParams): HttpParams {
    let params = new HttpParams()
      .set('Page', pageIndex.toString())
      .set('PageSize', pageSize.toString());

    const addParam = (
      condition: boolean,
      key: string,
      value: string | number | boolean
    ) => {
      if (condition) {
        params = params.set(key, String(value));
      }
    };

    addParam(!!sortOrder, 'sortOrder', sortOrder as number);
    addParam(!!sort, 'sort', sort as string);
    addParam(!!searchTerm, 'searchTerm', searchTerm as string);
    addParam(!!statusId, 'statusId', statusId as number);
    addParam(!!studentId, 'studentId', studentId as string);
    addParam(!!studentName, 'studentName', studentName as string);
    addParam(!!caseOwner, 'caseOwner', caseOwner as string);
    addParam(!!createdDate, 'createdDate', createdDate as string);
    addParam(!!modifiedDate, 'modifiedDate', modifiedDate as string);
    addParam(!!organizationId, 'OrganizationId', organizationId as number);
    addParam(!!intensityId, 'intensityId', intensityId as number);
    addParam(!!isOpen, 'isOpen', isOpen as boolean);
    addParam(
      isMyCases !== null && isMyCases !== undefined,
      'IsMyCases',
      isMyCases as boolean
    );
    addParam(
      isUnassigned !== null && isUnassigned !== undefined,
      'IsUnassignedCases',
      isUnassigned as boolean
    );
    addParam(
      IsInsightAdminTab !== null && IsInsightAdminTab !== undefined,
      'IsInsightAdminTab',
      IsInsightAdminTab as boolean
    );
    addParam(
      assessmentFba !== null && assessmentFba !== undefined,
      'assessmentFba',
      assessmentFba as unknown as boolean | number
    );
    return params;
  }

  getBIPList(queryParams: BIPQueryParams): Observable<BIPApiResponsePaginated> {
    if (
      !queryParams.organizationId &&
      !this.user_Data?.isSuperAdmin &&
      this.bipListFilterOrgId
    ) {
      queryParams = { ...queryParams, organizationId: this.bipListFilterOrgId };
    }
    const params = this.buildBIPQueryParams(queryParams);
    const requestKey = params.toString();
    const now = Date.now();

    if (now - this.lastBIPRequestTime < this.DEBOUNCE_TIME) {
      const currentValue = this.BIPSubject.getValue();
      if (currentValue) {
        return of(currentValue);
      }
    }

    this.lastBIPRequestTime = now;

    const activeRequest = this.activeBIPRequests.get(requestKey);
    if (activeRequest) {
      return activeRequest;
    }

    const request = this.http
      .get<BIPApiResponsePaginated>(`${this.API_URL}/Insight/list-bip-case`, {
        params,
      })
      .pipe(
        tap(response => {
          this.BIPSubject.next(response);
          this.totalCasesSubject.next(response?.result?.totalRows ?? 0);
        }),
        catchError(error => this.errorHandler.handleError(error)),
        share(),
        finalize(() => {
          this.activeBIPRequests.delete(requestKey);
        })
      );
    this.activeBIPRequests.set(requestKey, request);

    return request;
  }

  getAllBIPsForExport(
    queryParams: Partial<BIPQueryParams>
  ): Observable<BIPApiResponsePaginated> {
    const totalCases = this.totalCasesSubject.getValue() || 1000;

    const params = this.buildBIPQueryParams({
      pageIndex: 1,
      pageSize: totalCases,
      sortOrder: queryParams.sortOrder,
      sort: queryParams.sort,
      searchTerm: queryParams.searchTerm,
      organizationId: queryParams.organizationId,
      statusId: queryParams.statusId,
      studentId: queryParams.studentId,
      intensityId: queryParams.intensityId,
      isOpen: queryParams.isOpen,
      assessmentFba: queryParams.assessmentFba,
      studentName: queryParams.studentName,
      caseOwner: queryParams.caseOwner,
      createdDate: queryParams.createdDate,
      modifiedDate: queryParams.modifiedDate,
      IsInsightAdminTab: queryParams.IsInsightAdminTab,
      isMyCases: queryParams.isMyCases,
      isUnassigned: queryParams.isUnassigned,
    });

    return this.http
      .get<BIPApiResponsePaginated>(`${this.API_URL}/Insight/list-bip-case`, {
        params,
      })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  createIncident(
    payload: IncidentModel
  ): Observable<ApiResponse<IncidentModel>> {
    const requestPayload: Record<string, unknown> = { ...payload };
    const modifiedFields = payload._modifiedFields;

    if (modifiedFields && modifiedFields.length > 0) {
      requestPayload['_modifiedFields'] = modifiedFields;
    }

    return this.http
      .post<
        ApiResponse<IncidentModel>
      >(`${this.API_URL}/Incident/create`, requestPayload)
      .pipe(
        tap(() => {
          this.getIncidentList({
            pageIndex: 1,
            pageSize: 10,
            caseId: payload.caseId,
          }).subscribe();
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }
  updateIncident(
    payload: IncidentModel,
    id: number
  ): Observable<ApiResponse<IncidentModel>> {
    const requestPayload: Record<string, unknown> = { ...payload };
    const modifiedFields = payload._modifiedFields;

    if (modifiedFields && modifiedFields.length > 0) {
      requestPayload['_modifiedFields'] = modifiedFields;
    }

    const params = new HttpParams().set('id', id.toString());
    return this.http
      .put<
        ApiResponse<IncidentModel>
      >(`${this.API_URL}/Incident/update`, requestPayload, { params })
      .pipe(
        tap(() => {
          this.getIncidentList({
            pageIndex: 1,
            pageSize: 10,
            caseId: payload.caseId,
          }).subscribe();
          this.getIncidentById(id).subscribe();
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }
  private buildIncidentQueryParams(
    queryParams: IncidentQueryParams
  ): HttpParams {
    let params = new HttpParams()
      .set('Page', queryParams.pageIndex.toString())
      .set('PageSize', queryParams.pageSize.toString());

    if (queryParams.sortOrder) {
      params = params.set('sortOrder', queryParams.sortOrder);
    }

    if (queryParams.sort) {
      params = params.set('sort', queryParams.sort);
    }
    if (queryParams.caseId) {
      params = params.set('CaseId', queryParams.caseId);
    }

    if (queryParams.id) {
      params = params.set('id', queryParams.id.toString());
    }
    if (queryParams.title) {
      params = params.set('title', queryParams.title.toString());
    }
    if (queryParams.description) {
      params = params.set('description', queryParams.description.toString());
    }
    if (queryParams.dateTime) {
      params = params.set('dateTime', queryParams.dateTime.toString());
    }
    if (queryParams.instanceCount) {
      params = params.set(
        'instanceCount',
        queryParams.instanceCount.toString()
      );
    }
    if (queryParams.incidentCreator) {
      params = params.set(
        'incidentCreator',
        queryParams.incidentCreator.toString()
      );
    }
    return params;
  }

  getIncidentList(
    queryParams: IncidentQueryParams
  ): Observable<IncidentApiResponsePaginated> {
    const params = this.buildIncidentQueryParams(queryParams);
    const requestKey = params.toString();
    const now = Date.now();

    if (now - this.lastIncidentRequestTime < this.DEBOUNCE_TIME) {
      const currentValue = this.IncidentSubject.getValue();
      if (currentValue) {
        return of(currentValue);
      }
    }

    this.lastIncidentRequestTime = now;

    const activeRequest = this.activeIncidentRequests.get(requestKey);
    if (activeRequest) {
      return activeRequest;
    }

    const request = this.http
      .get<IncidentApiResponsePaginated>(`${this.API_URL}/Incident/list`, {
        params,
      })
      .pipe(
        tap(response => this.IncidentSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error)),
        share(),
        finalize(() => {
          this.activeIncidentRequests.delete(requestKey);
        })
      );
    this.activeIncidentRequests.set(requestKey, request);

    return request;
  }

  getIncidentById(id: number): Observable<ApiResponse<IncidentList>> {
    return this.http
      .get<ApiResponse<IncidentList>>(`${this.API_URL}/Incident/${id}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  deleteCase(caseId: number) {
    return this.http
      .delete<ApiResponse<void>>(`${this.API_URL}/Insight/${caseId}`, {})
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  convertToBIp(payload: ConvertToBIP) {
    const requestPayload: Record<string, unknown> = {
      caseId: payload.caseId,
      bipTemplateId: payload.bipTemplateId,
    };

    return this.http
      .post<
        ApiResponse<void>
      >(`${this.API_URL}/Insight/convert-bip-case?caseId=${payload.caseId}&bipTemplateId=${payload.bipTemplateId}`, requestPayload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
  submitBehaviourFuncation(
    payload: BehaviorFuncation
  ): Observable<ApiResponse<void>> {
    return this.http
      .post<
        ApiResponse<void>
      >(`${this.API_URL}/BehaviorFunction/create`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getBehvaiourAssessmentLookup() {
    return this.http
      .get<FunctionalAssessmentResponse>(
        `${this.API_URL}/BehaviorAssessment/lookups`
      )
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  submitBehaviouralAssessment(
    payload: FunctionalAssessmentModelResponse
  ): Observable<ApiResponse<void>> {
    return this.http
      .post<
        ApiResponse<void>
      >(`${this.API_URL}/BehaviorAssessment/create`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getBehvaiourGraphData(id: number) {
    return this.http
      .get<
        ApiResponses<BehaviouralAssessmentGraphData>
      >(`${this.API_URL}/BehaviorAssessment/get-assesment-graph/${id}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getBehaviourAssessment(id: number) {
    return this.http
      .get<
        ApiResponse<FunctionalAssessmentModelResponse>
      >(`${this.API_URL}/BehaviorAssessment/list?caseBehaviorId=${id}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  updateStudentImage(
    studentId: number,
    file: File,
    modifiedFields?: AuditFieldChange[]
  ) {
    const formData = new FormData();
    formData.append('ImageFile', file);
    formData.append('StudentId', studentId.toString());

    if (modifiedFields && modifiedFields.length > 0) {
      formData.append('_modifiedFields', JSON.stringify(modifiedFields));
    }

    return this.http
      .post<
        ApiResponse<string>
      >(`${this.API_URL}/student/upload-student-image`, formData)
      .pipe(
        map(response => {
          if (!response.result) {
            throw new Error('Logo creation failed');
          }
          return response.result;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  deleteStudentImage(studentId: number) {
    return this.http
      .delete<
        ApiResponse<string>
      >(`${this.API_URL}/student/remove-student-image?studentId=${studentId}`)
      .pipe(
        map(response => {
          return response;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getStrategyByCaseId(caseId: number) {
    return this.http
      .get<ApiStratergyResponses>(
        `${this.API_URL}/Insight/list-strategy-by-case-id?caseId=${caseId}`
      )
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  moveStudents(payload: MoveStudentRequest): Observable<ApiRespones<void>> {
    return this.http
      .post<
        ApiRespones<void>
      >(`${this.API_URL}/Student/insight-admin-move-student`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
  updateCaseStatus(caseId: number, statusId: number) {
    return this.http
      .post<
        ApiRespones<void>
      >(`${environment.apiUrl}/Insight/update-case-status?caseId=${caseId}&statusId=${statusId}`, {})
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  unAssignCase(payload: UnAssignCase) {
    return this.http
      .post<
        ApiRespones<void>
      >(`${this.API_URL}/insight/case-owner/unassign`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
  unAssignBulkCase(payload: UnAssignCase) {
    return this.http
      .post<
        ApiRespones<void>
      >(`${this.API_URL}/insight/case-owner/unassign/bulk`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
  assignBulkCase(payload: UnAssignCase) {
    return this.http
      .post<
        ApiRespones<void>
      >(`${this.API_URL}/insight/case-owner/reassign/bulk`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
  getUsersForAssign(queryParams: OrgUserQueryParams) {
    const params = this.buildUserQueryParams(queryParams);
    return this.http
      .get<ApiResponse<User[]>>(
        `${this.API_URL}/insight/case-owner/get-users`,
        {
          params,
        }
      )
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  private buildUserQueryParams({
    pageIndex,
    pageSize,
    sortOrder,
    sort,
    searchTerm,
    organizationId,
    status,
  }: OrgUserQueryParams): HttpParams {
    let params = new HttpParams()
      .set('Page', pageIndex.toString())
      .set('PageSize', pageSize.toString());

    if (sortOrder) {
      params = params.set('sortOrder', sortOrder);
    }

    if (sort) {
      params = params.set('sort', sort);
    }

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    if (organizationId) {
      params = params.set('organizationId', organizationId.toString());
    }

    if (status) {
      params = params.set('status', status.toString());
    }

    return params;
  }
  /**
   * Toggle case notification preference for a case
   * @param caseId number
   * @param isEnabled boolean
   */
  toggleCaseNotificationPreference(caseId: number, isEnabled: boolean) {
    return this.http
      .put(
        `${this.API_URL}/casenotificationpreference/update-case-notification-prefernce?caseId=${caseId}`,
        {
          isEnabled,
        }
      )
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
}
